var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { createLabeledBox } from './primitives';
export { centerTextX, measureTextWidth } from './primitives';
export function num(props, key, fallback) {
    var v = props[key];
    return typeof v === 'number' ? v : fallback;
}
export function str(props, key, fallback) {
    if (fallback === void 0) { fallback = ''; }
    var v = props[key];
    return typeof v === 'string' ? v : fallback;
}
export function bool(props, key, fallback) {
    if (fallback === void 0) { fallback = false; }
    var v = props[key];
    return typeof v === 'boolean' ? v : fallback;
}
export function arr(props, key, fallback) {
    if (fallback === void 0) { fallback = []; }
    var v = props[key];
    return Array.isArray(v) ? v : fallback;
}
export function getDiagramState(node) {
    var _a;
    var state = (_a = node.metadata) === null || _a === void 0 ? void 0 : _a.diagramState;
    if (state && typeof state === 'object')
        return state;
    return {};
}
export function setDiagramState(node, patch) {
    node.metadata.diagramState = __assign(__assign({}, getDiagramState(node)), patch);
}
/** Create a diagram group with metadata for JSON round-trip. */
export function createDiagramGroup(app, type, props, extra) {
    if (extra === void 0) { extra = {}; }
    var group = app.group(__assign(__assign(__assign({}, props), { metadata: __assign({ diagramType: type, diagramState: __assign({}, props) }, (props.demoId != null ? { demoId: props.demoId } : {})) }), extra));
    setDiagramState(group, __assign({}, props));
    return group;
}
/** Serialize a diagram node back to JSON. */
export function diagramToJSON(node) {
    var type = str(node.metadata, 'diagramType', 'diagram');
    var state = getDiagramState(node);
    return {
        type: type,
        props: __assign({ x: node.x, y: node.y }, state),
    };
}
/** Seeded PRNG for reproducible layouts. */
export function seededRandom(seed) {
    var s = seed >>> 0;
    return function () {
        s = (s * 1664525 + 1013904223) >>> 0;
        return s / 0xffffffff;
    };
}
export function readCanvasSize(options, fallbackW, fallbackH) {
    if (fallbackW === void 0) { fallbackW = 800; }
    if (fallbackH === void 0) { fallbackH = 500; }
    return {
        width: typeof options.width === 'number' ? options.width : fallbackW,
        height: typeof options.height === 'number' ? options.height : fallbackH,
    };
}
/** Compute responsive grid columns and spacing for diagram auto-layout */
export function resolveGridLayout(nodeCount, canvasW, canvasH, nodeW, nodeH, minPadding) {
    if (nodeW === void 0) { nodeW = 130; }
    if (nodeH === void 0) { nodeH = 80; }
    if (minPadding === void 0) { minPadding = 16; }
    var tight = canvasW < 520 || canvasH < 360;
    var maxCols = tight
        ? Math.max(1, Math.floor((canvasW - minPadding * 2) / (nodeW * 0.9)))
        : Math.max(2, Math.ceil(Math.sqrt(nodeCount * (canvasW / Math.max(canvasH, 1)))));
    var cols = Math.max(1, Math.min(maxCols, nodeCount));
    var rows = Math.ceil(nodeCount / cols);
    var paddingX = Math.max(minPadding, Math.round(canvasW * 0.04));
    var paddingY = Math.max(minPadding, Math.round(canvasH * 0.05));
    var availW = Math.max(nodeW, canvasW - paddingX * 2);
    var availH = Math.max(nodeH, canvasH - paddingY * 2);
    var cellW = cols <= 1 ? nodeW : Math.max(nodeW * 0.72, (availW - nodeW) / Math.max(cols - 1, 1));
    var cellH = rows <= 1 ? nodeH : Math.max(nodeH * 0.72, (availH - nodeH) / Math.max(rows - 1, 1));
    return { cols: cols, cellW: cellW, cellH: cellH, paddingX: paddingX, paddingY: paddingY };
}
/** Auto-place diagram nodes on a grid when positions are missing */
export function autoLayoutNodes(nodes, cols, cellW, cellH, paddingX, paddingY) {
    if (cols === void 0) { cols = 3; }
    if (cellW === void 0) { cellW = 150; }
    if (cellH === void 0) { cellH = 80; }
    if (paddingX === void 0) { paddingX = 24; }
    if (paddingY === void 0) { paddingY = 24; }
    var needs = nodes.some(function (n) { return n.x === undefined || n.y === undefined; });
    if (!needs)
        return;
    nodes.forEach(function (n, i) {
        if (n.x === undefined)
            n.x = paddingX + (i % cols) * cellW;
        if (n.y === undefined)
            n.y = paddingY + Math.floor(i / cols) * cellH;
    });
}
/** Responsive grid auto-layout using canvas dimensions from options */
export function autoLayoutNodesResponsive(nodes, canvasW, canvasH, nodeW, nodeH) {
    if (nodeW === void 0) { nodeW = 130; }
    if (nodeH === void 0) { nodeH = 80; }
    var needs = nodes.some(function (n) { return n.x === undefined || n.y === undefined; });
    if (!needs)
        return;
    var _a = resolveGridLayout(nodes.length, canvasW, canvasH, nodeW, nodeH), cols = _a.cols, cellW = _a.cellW, cellH = _a.cellH, paddingX = _a.paddingX, paddingY = _a.paddingY;
    nodes.forEach(function (n, i) {
        if (n.x === undefined)
            n.x = paddingX + (i % cols) * cellW;
        if (n.y === undefined)
            n.y = paddingY + Math.floor(i / cols) * cellH;
    });
}
/**
 * Push overlapping diagram nodes apart so symbols do not stack on render.
 * Uses card metadata sizes when available; iterates a few times for stability.
 * Skips mind-map / org nested trees (call only on flat diagram node lists).
 */
export function separateOverlappingNodes(nodes, opts) {
    var e_1, _a, e_2, _b;
    var _c, _d, _e;
    if (nodes.length < 2)
        return;
    var gap = (_c = opts === null || opts === void 0 ? void 0 : opts.gap) !== null && _c !== void 0 ? _c : 16;
    var iterations = (_d = opts === null || opts === void 0 ? void 0 : opts.iterations) !== null && _d !== void 0 ? _d : 8;
    var canvasW = opts === null || opts === void 0 ? void 0 : opts.canvasW;
    var canvasH = opts === null || opts === void 0 ? void 0 : opts.canvasH;
    var sizeOf = function (n) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        var w = (_d = (_b = (_a = n.metadata) === null || _a === void 0 ? void 0 : _a.diagramCardWidth) !== null && _b !== void 0 ? _b : (_c = n.metadata) === null || _c === void 0 ? void 0 : _c.orgCardWidth) !== null && _d !== void 0 ? _d : 120;
        var h = (_h = (_f = (_e = n.metadata) === null || _e === void 0 ? void 0 : _e.diagramCardHeight) !== null && _f !== void 0 ? _f : (_g = n.metadata) === null || _g === void 0 ? void 0 : _g.orgCardHeight) !== null && _h !== void 0 ? _h : 48;
        return { w: Math.max(24, w), h: Math.max(20, h) };
    };
    for (var iter = 0; iter < iterations; iter++) {
        var moved = false;
        for (var i = 0; i < nodes.length; i++) {
            for (var j = i + 1; j < nodes.length; j++) {
                var a = nodes[i];
                var b = nodes[j];
                var sa = sizeOf(a);
                var sb = sizeOf(b);
                var ax1 = a.x;
                var ay1 = a.y;
                var ax2 = a.x + sa.w;
                var ay2 = a.y + sa.h;
                var bx1 = b.x;
                var by1 = b.y;
                var bx2 = b.x + sb.w;
                var by2 = b.y + sb.h;
                var overlapX = Math.min(ax2, bx2) - Math.max(ax1, bx1);
                var overlapY = Math.min(ay2, by2) - Math.max(ay1, by1);
                if (overlapX <= -gap || overlapY <= -gap)
                    continue;
                // Separate along the smaller penetration axis
                var acx = a.x + sa.w / 2;
                var acy = a.y + sa.h / 2;
                var bcx = b.x + sb.w / 2;
                var bcy = b.y + sb.h / 2;
                var needX = overlapX + gap;
                var needY = overlapY + gap;
                if (needX < needY) {
                    var dir = bcx >= acx ? 1 : -1;
                    var push = needX / 2;
                    a.x -= dir * push;
                    b.x += dir * push;
                }
                else {
                    var dir = bcy >= acy ? 1 : -1;
                    var push = needY / 2;
                    a.y -= dir * push;
                    b.y += dir * push;
                }
                moved = true;
            }
        }
        if (canvasW != null || canvasH != null) {
            try {
                for (var nodes_1 = (e_1 = void 0, __values(nodes)), nodes_1_1 = nodes_1.next(); !nodes_1_1.done; nodes_1_1 = nodes_1.next()) {
                    var n = nodes_1_1.value;
                    var s = sizeOf(n);
                    if (canvasW != null)
                        n.x = Math.max(8, Math.min(canvasW - s.w - 8, n.x));
                    if (canvasH != null)
                        n.y = Math.max(8, Math.min(canvasH - s.h - 8, n.y));
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (nodes_1_1 && !nodes_1_1.done && (_a = nodes_1.return)) _a.call(nodes_1);
                }
                finally { if (e_1) throw e_1.error; }
            }
        }
        if (!moved)
            break;
    }
    try {
        for (var nodes_2 = __values(nodes), nodes_2_1 = nodes_2.next(); !nodes_2_1.done; nodes_2_1 = nodes_2.next()) {
            var n = nodes_2_1.value;
            (_e = n.markDirty) === null || _e === void 0 ? void 0 : _e.call(n);
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (nodes_2_1 && !nodes_2_1.done && (_b = nodes_2.return)) _b.call(nodes_2);
        }
        finally { if (e_2) throw e_2.error; }
    }
}
/** Union bounds of diagram content nodes (excludes connector layers). */
/**
 * Axis-aligned content box in the node's own local space.
 * Avoids Group.getBounds() which returns world coordinates and would
 * double-count when composed with parent offsets in fitToBounds.
 */
function localContentBox(node) {
    var e_3, _a;
    var _b, _c, _d, _e, _f, _g, _h;
    var cardW = (_c = (_b = node.metadata) === null || _b === void 0 ? void 0 : _b.orgCardWidth) !== null && _c !== void 0 ? _c : (_d = node.metadata) === null || _d === void 0 ? void 0 : _d.diagramCardWidth;
    var cardH = (_f = (_e = node.metadata) === null || _e === void 0 ? void 0 : _e.orgCardHeight) !== null && _f !== void 0 ? _f : (_g = node.metadata) === null || _g === void 0 ? void 0 : _g.diagramCardHeight;
    if (typeof cardW === 'number' && typeof cardH === 'number' && cardW > 0 && cardH > 0) {
        var tap = typeof ((_h = node.metadata) === null || _h === void 0 ? void 0 : _h.diagramTapPad) === 'number' ? node.metadata.diagramTapPad : 0;
        return { x: 0, y: -tap, width: cardW, height: cardH + tap };
    }
    var group = node;
    if (Array.isArray(group.children) && group.children.length > 0) {
        var minX = Infinity;
        var minY = Infinity;
        var maxX = -Infinity;
        var maxY = -Infinity;
        try {
            for (var _j = __values(group.children), _k = _j.next(); !_k.done; _k = _j.next()) {
                var child = _k.value;
                if (child.visible === false)
                    continue;
                var lb = localContentBox(child);
                minX = Math.min(minX, child.x + lb.x);
                minY = Math.min(minY, child.y + lb.y);
                maxX = Math.max(maxX, child.x + lb.x + lb.width);
                maxY = Math.max(maxY, child.y + lb.y + lb.height);
            }
        }
        catch (e_3_1) { e_3 = { error: e_3_1 }; }
        finally {
            try {
                if (_k && !_k.done && (_a = _j.return)) _a.call(_j);
            }
            finally { if (e_3) throw e_3.error; }
        }
        if (!Number.isFinite(minX))
            return { x: 0, y: 0, width: 0, height: 0 };
        return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    }
    return node.getBounds();
}
function diagramContentBounds(group) {
    var e_4, _a, e_5, _b;
    var _c, _d, _e;
    var nodes = [];
    var walk = function (parent) {
        var e_6, _a;
        var _b, _c, _d, _e, _f;
        try {
            for (var _g = __values(parent.children), _h = _g.next(); !_h.done; _h = _g.next()) {
                var child = _h.value;
                if (((_b = child.metadata) === null || _b === void 0 ? void 0 : _b.diagramId) ||
                    ((_c = child.metadata) === null || _c === void 0 ? void 0 : _c.orgNode) ||
                    ((_d = child.metadata) === null || _d === void 0 ? void 0 : _d.symbolType) ||
                    ((_e = child.metadata) === null || _e === void 0 ? void 0 : _e.pipelineStatus) !== undefined) {
                    nodes.push(child);
                }
                if ('children' in child && ((_f = child.children) === null || _f === void 0 ? void 0 : _f.length)) {
                    walk(child);
                }
            }
        }
        catch (e_6_1) { e_6 = { error: e_6_1 }; }
        finally {
            try {
                if (_h && !_h.done && (_a = _g.return)) _a.call(_g);
            }
            finally { if (e_6) throw e_6.error; }
        }
    };
    walk(group);
    var sources = nodes.length > 0 ? __spreadArray([], __read(nodes), false) : group.children.filter(function (c) { return c.zIndex >= 0; });
    // Include root-level chrome (CAN bus rails, labels, terminators) so fit does not crop them
    if (nodes.length > 0) {
        var seen = new Set(sources);
        try {
            for (var _f = __values(group.children), _g = _f.next(); !_g.done; _g = _f.next()) {
                var child = _g.value;
                if ((_c = child.metadata) === null || _c === void 0 ? void 0 : _c.diagramEdgeLayer)
                    continue;
                if (seen.has(child))
                    continue;
                if (((_d = child.metadata) === null || _d === void 0 ? void 0 : _d.diagramId) || ((_e = child.metadata) === null || _e === void 0 ? void 0 : _e.orgNode))
                    continue;
                sources.push(child);
                seen.add(child);
            }
        }
        catch (e_4_1) { e_4 = { error: e_4_1 }; }
        finally {
            try {
                if (_g && !_g.done && (_a = _f.return)) _a.call(_f);
            }
            finally { if (e_4) throw e_4.error; }
        }
    }
    if (sources.length === 0)
        return group.getBounds();
    var posInRoot = function (node) {
        var x = 0;
        var y = 0;
        var cur = node;
        while (cur && cur !== group) {
            x += cur.x;
            y += cur.y;
            cur = cur.parent;
        }
        return { x: x, y: y };
    };
    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    try {
        for (var sources_1 = __values(sources), sources_1_1 = sources_1.next(); !sources_1_1.done; sources_1_1 = sources_1.next()) {
            var child = sources_1_1.value;
            var b = localContentBox(child);
            var p = posInRoot(child);
            minX = Math.min(minX, p.x + b.x);
            minY = Math.min(minY, p.y + b.y);
            maxX = Math.max(maxX, p.x + b.x + b.width);
            maxY = Math.max(maxY, p.y + b.y + b.height);
        }
    }
    catch (e_5_1) { e_5 = { error: e_5_1 }; }
    finally {
        try {
            if (sources_1_1 && !sources_1_1.done && (_b = sources_1.return)) _b.call(sources_1);
        }
        finally { if (e_5) throw e_5.error; }
    }
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}
/** Scale and translate a diagram group to fit within canvas bounds */
export function fitDiagramToBounds(group, canvasW, canvasH, padding, options) {
    var _a, _b;
    if (padding === void 0) { padding = 20; }
    if (options === void 0) { options = {}; }
    var allowScaleUp = (_a = options.allowScaleUp) !== null && _a !== void 0 ? _a : true;
    var maxScaleUp = (_b = options.maxScaleUp) !== null && _b !== void 0 ? _b : 2.1;
    var b = diagramContentBounds(group);
    var contentW = Math.max(b.width, 1);
    var contentH = Math.max(b.height, 1);
    var availW = canvasW - padding * 2;
    var availH = canvasH - padding * 2;
    var scale = Math.min(availW / contentW, availH / contentH);
    if (scale > 1 && allowScaleUp)
        scale = Math.min(scale, maxScaleUp);
    else if (scale > 1)
        scale = 1;
    var offsetX = padding + (availW - contentW * scale) / 2 - b.x * scale;
    var offsetY = padding + (availH - contentH * scale) / 2 - b.y * scale;
    group.scaleX = scale;
    group.scaleY = scale;
    group.x = offsetX;
    group.y = offsetY;
    group.markDirty();
    return { scale: scale, offsetX: offsetX, offsetY: offsetY };
}
/** Build a labeled node box used across diagram types. */
export function createNodeBox(app, label, width, height, style) {
    if (style === void 0) { style = {}; }
    return createLabeledBox(app, label, width, height, style);
}
/** Apply positions from a map to diagram node groups by diagramId. */
export function applyPositions(group, positions) {
    var e_7, _a;
    var _b;
    try {
        for (var _c = __values(group.children), _d = _c.next(); !_d.done; _d = _c.next()) {
            var child = _d.value;
            var id = (_b = child.metadata) === null || _b === void 0 ? void 0 : _b.diagramId;
            if (id && positions.has(id)) {
                var pos = positions.get(id);
                child.x = pos.x;
                child.y = pos.y;
                child.markDirty();
            }
        }
    }
    catch (e_7_1) { e_7 = { error: e_7_1 }; }
    finally {
        try {
            if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
        }
        finally { if (e_7) throw e_7.error; }
    }
}
/** Collect diagram nodes and edges from generic data. */
export function normalizeDiagramData(data) {
    var _a, _b;
    return {
        nodes: (_a = data.nodes) !== null && _a !== void 0 ? _a : [],
        edges: (_b = data.edges) !== null && _b !== void 0 ? _b : [],
    };
}
